import PropTypes from "prop-types";
import { useState, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import _ from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import colors from "assets/theme/base/colors";
import Icon from "@mui/material/Icon";

import SuiTagInput from "components/SuiTagInput";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiSelect from "components/SuiSelect";
import FormField from "components/FormField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { PostType } from "api";

function NewOrEditPage({ mode }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditing = !!id;
  const TITLE = t(isEditing ? "Edit Post Type" : "New Post Type");
  const history = useHistory();
  const [postType, setPostType] = useState({
    status: "draft",
  });
  const [postTypeOriginal, setPostTypeOriginal] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandSeo, setExpandSeo] = useState(true);
  const [expandPushNotification, setExpandPushNotification] = useState(true);
  const [currLang, setCurrLang] = useState("SC");

  const getCurrLangLabel = () => {
    if (currLang === "TC") {
      return t("Traditional Chinese");
    }
    if (currLang === "SC") {
      return t("Simplified Chinese");
    }
    return t("English");
  };

  useEffect(() => {
    if (!isEditing) return;
    (async () => {
      setIsLoading(true);
      try {
        const { data } = await PostType.get(id);
        setPostType(data);
        setPostTypeOriginal(data);
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    })();
  }, []);

  const readPostType = (prop) => {
    if (prop === "css" || prop === "status" || prop === "isSendPushNotification") {
      return postType[prop];
    }
    return postType[prop + currLang] || "";
  };
  const readPostTypeOriginal = (prop) => {
    if (prop === "css" || prop === "status" || prop === "isSendPushNotification") {
      return postTypeOriginal[prop];
    }
    return postTypeOriginal[prop + currLang] || "";
  };
  const updatePostType = (prop, value) => {
    const n = { ...postType };
    if (prop === "css" || prop === "status" || prop === "isSendPushNotification") {
      n[prop] = value;
    } else {
      n[prop + currLang] = value;
    }
    setPostType(n);
  };

  const getPushNotificationLabel = () => {
    const push = readPostType('isSendPushNotification');
    if (push === 1) {
      return t("Enable");
    }
    if (push === 2) {
      return t("Disable");
    }
    if (push === 3) {
      return t("Optional");
    }
    return "";
  };

  const getPageStatusLabel = () => {
    if (postType.status === "3") {
      return "Published";
    }
    if (postType.status === "2") {
      return "Scheduled";
    }
    return "Draft";
  };

  const categories = useCallback(() => {
    const cats = readPostType("category");
    if (!cats) return [];
    return cats.split(",")
  }, [postType, currLang]);
  const subcategories = useCallback(() => {
    const cats = readPostType("subCategory");
    if (!cats) return [];
    return cats.split(",")
  }, [postType, currLang]);

  const save = async () => {
    let error = "";
    console.log({ postType })
    if (!isEditing) {
      if (postType.title || postType.slug) {
        if (!postType.title) {
          error = "Missing title";
        } else if (!postType.slug) {
          error = "Missing slug";
        }
      } else if (postType.titleTC || postType.slugTC) {
        if (!postType.titleTC) {
          error = "Missing title";
        } else if (!postType.slugTC) {
          error = "Missing slug";
        }
      } else if (postType.titleSC || postType.slugSC) {
        if (!postType.titleSC) {
          error = "Missing title";
        } else if (!postType.slugSC) {
          error = "Missing slug";
        }
      } else {
        error = "Missing title";
      }
    }
    if (error) {
      Swal.fire(t(error));
    } else {
      try {
        const data = {};
        [
          "id",
          "title",
          "titleTC",
          "titleSC",
          "slug",
          "slugTC",
          "slugSC",
          "category",
          "categoryTC",
          "categorySC",
          "subCategory",
          "subCategoryTC",
          "subCategorySC",
          "description",
          "descriptionTC",
          "descriptionSC",
          "isSendPushNotification",
          "css",
          "metaTitle",
          "metaTitleTC",
          "metaTitleSC",
          "metaKeyword",
          "metaKeywordTC",
          "metaKeywordSC",
          "metaDescription",
          "metaDescriptionTC",
          "metaDescriptionSC",
        ].forEach((v) => {
          data[v] = postType[v];
        });
        if (isEditing) {
          await PostType.update(data);
        } else {
          await PostType.create(data);
        }
        history.goBack();
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={9}>
              <Card style={{ overflow: "visible" }}>
                <SuiBox p={3}>
                  <SuiBox>
                    <SuiBox
                      py={1}
                      style={{
                        float: "right",
                        minWidth: 200,
                      }}
                    >
                      <SuiSelect
                        placeholder="Choose a type"
                        value={{
                          label: getCurrLangLabel(),
                          value: currLang,
                        }}
                        options={[
                          {
                            label: t("Traditional Chinese"),
                            value: "TC",
                          },
                          {
                            label: t("Simplified Chinese"),
                            value: "SC",
                          },
                          {
                            label: t("English"),
                            value: "",
                          },
                        ]}
                        onChange={(option) => {
                          setCurrLang(option.value);
                        }}
                      />
                    </SuiBox>
                    <SuiTypography variant="h5">{TITLE}</SuiTypography>
                    <SuiTypography variant="button" fontWeight="regular" color="text">
                      {t(`${isEditing ? "Edit" : "New"} Page`)}
                    </SuiTypography>
                  </SuiBox>
                  <Divider />
                  {(!isEditing || (isEditing && !isLoading)) && (
                    <Grid item>
                      <FormField
                        label={t("title")}
                        disabled={(isEditing && readPostTypeOriginal("title")) || mode === "view"}
                        value={readPostType("title")}
                        onChange={(e) => {
                          updatePostType("title", e.target.value);
                        }}
                      />
                      <FormField
                        label={t("slug")}
                        disabled={(isEditing && readPostTypeOriginal("slug")) || mode === "view"}
                        value={readPostType("slug")}
                        onChange={(e) => {
                          updatePostType("slug", e.target.value);
                        }}
                      />
                      {([3, 4, 5, 6, 7, 8].indexOf(postType.id) !== -1 || !(isEditing || mode === "view")) &&
                        <SuiBox mb={3}>
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            {t("Categories")}
                          </SuiTypography>
                          <SuiTagInput
                            size={"large"}
                            tags={categories()}
                            placeholder={" "}
                            onChange={tags => {
                              updatePostType("category", tags.join(","))
                            }}
                            readOnly={mode === "view"}
                          />
                        </SuiBox>
                      }
                      {(postType.id === 3 || !(isEditing || mode === "view")) &&
                        <SuiBox mb={3}>
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            {t("Sub-categories")}
                          </SuiTypography>
                          <SuiTagInput
                            size={"large"}
                            tags={subcategories()}
                            placeholder={" "}
                            onChange={tags => {
                              updatePostType("subCategory", tags.join(","))
                            }}
                            readOnly={mode === "view"}
                          />
                        </SuiBox>
                      }
                      <FormField
                        label={t("Description")}
                        placeholder={t("Enter Description")}
                        multiline
                        minRows={5}
                        onChange={(e) => {
                          updatePostType("description", e.target.value);
                        }}
                        value={readPostType("description")}
                        disabled={mode === "view"}
                      />
                      <FormField
                        label="CSS"
                        placeholder={t("Enter CSS")}
                        multiline
                        minRows={5}
                        onChange={(e) => {
                          updatePostType("css", e.target.value);
                        }}
                        value={readPostType("css")}
                        disabled={mode === "view"}
                      />
                      <SuiBox
                        p={3}
                        py={1.5}
                        style={{
                          background: colors.background.default,
                          margin: "0 -24px",
                          position: "relative",
                          fontSize: 12,
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setExpandPushNotification(!expandPushNotification);
                        }}
                      >
                        {t("Push Notification")}
                        <SuiBox
                          p={2}
                          py={1.5}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                          }}
                        >
                          <SuiTypography>
                            <Icon sx={{ fontWeight: "bold" }}>
                              {expandPushNotification ? "expand_less" : "expand_more"}
                            </Icon>
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                      {expandPushNotification && (
                        <SuiBox
                          pb={2}
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography component="label" variant="caption" fontWeight="bold">
                              {t("Push Notification Setting")}
                            </SuiTypography>
                          </SuiBox>
                          <SuiSelect
                            value={{
                              label: t(_.capitalize(t(getPushNotificationLabel()))),
                              value: "",
                            }}
                            options={[
                              {
                                label: t("Enable"),
                                value: 1,
                              },
                              {
                                label: t("Disable"),
                                value: 2,
                              },
                              {
                                label: t("Optional"),
                                value: 3,
                              },
                            ]}
                            onChange={(option) => {
                              updatePostType("isSendPushNotification", option.value);
                            }}
                            isDisabled={mode === "view"}
                          />
                        </SuiBox>
                      )}
                      <SuiBox
                        p={3}
                        py={1.5}
                        style={{
                          background: colors.background.default,
                          margin: "0 -24px",
                          position: "relative",
                          fontSize: 12,
                          fontWeight: "bold",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setExpandSeo(!expandSeo);
                        }}
                      >
                        {t("Search Engine Optimization")}
                        <SuiBox
                          p={2}
                          py={1.5}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                          }}
                        >
                          <SuiTypography>
                            <Icon sx={{ fontWeight: "bold" }}>
                              {expandSeo ? "expand_less" : "expand_more"}
                            </Icon>
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                      {expandSeo && (
                        <>
                          <FormField
                            label={t("Meta Title")}
                            placeholder={t("Enter Meta Title")}
                            onChange={(e) => {
                              updatePostType("metaTitle", e.target.value);
                            }}
                            value={readPostType("metaTitle")}
                            disabled={mode === "view"}
                          />
                          <FormField
                            label={t("Meta Keywords")}
                            placeholder={t("Enter Meta Keywords")}
                            multiline
                            minRows={5}
                            onChange={(e) => {
                              updatePostType("metaKeyword", e.target.value);
                            }}
                            value={readPostType("metaKeyword")}
                            disabled={mode === "view"}
                          />
                          <FormField
                            label={t("Meta Description")}
                            placeholder={t("Enter Meta Description")}
                            multiline
                            minRows={5}
                            onChange={(e) => {
                              updatePostType("metaDescription", e.target.value);
                            }}
                            value={readPostType("metaDescription")}
                            disabled={mode === "view"}
                          />
                        </>
                      )}
                    </Grid>
                  )}
                </SuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={3}>
              <TableContainer style={{ padding: 5 }}>
                <Table>
                  <TableHead>
                    <SuiBox component="tr" width="max-content" display="block" mb={1.5}>
                      <SuiTypography variant="h5" component="td">
                        {t("Page Settings")}
                      </SuiTypography>
                    </SuiBox>
                    <Divider />
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ border: 0 }}>
                        <SuiBox style={{ textAlign: "right" }}>
                          <SuiSelect
                            value={{
                              label: t(_.capitalize(getPageStatusLabel())),
                              value: "",
                            }}
                            options={[
                              {
                                label: t("Draft"),
                                value: "1",
                              },
                              {
                                label: t("Scheduled"),
                                value: "2",
                              },
                              {
                                label: t("Published"),
                                value: "3",
                              },
                            ]}
                            onChange={(option) => {
                              updatePostType("status", option.value);
                            }}
                            isDisabled={mode === "view"}
                          />
                        </SuiBox>
                        <SuiBox mt={7} mb={3} style={{ textAlign: "right" }}>
                          <SuiBox mt={7} mb={3} style={{ fontSize: 12 }} display="inline">
                            {postType.updatedAt && (
                              <>
                                {t("Last edit")}:{" "}
                                {moment(postType.updatedAt).format("YYYY-MM-DD hh:mm:ss A")}
                              </>
                            )}
                          </SuiBox>
                          <SuiBox ml={2} display="inline">
                            <SuiButton variant="gradient" color="info" onClick={save}>
                              {t("save")}
                            </SuiButton>
                          </SuiBox>
                        </SuiBox>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </SuiBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

NewOrEditPage.defaultProps = {
  mode: "view",
};
NewOrEditPage.propTypes = {
  mode: PropTypes.string,
};

export default NewOrEditPage;
